import React from 'react';

import Button from 'views/site/Button.js';
import Icon from 'views/site/Icon.js';
import Theme from 'styles/Theme.js';
import Utils from 'files/Utils.js';

/*
 * Graci
 *
 * The AI assistant section, ported from the storefront — content on the left,
 * a mock conversation on the right, mobile reordered so the heading frames the
 * chat rather than dropping it in cold.
 *
 * One meaningful difference. On the storefront this section is a placeholder:
 * its button fires an alert saying GRACI "is being extended to this site". Here
 * GRACI is real and already shipped — it is the search and answer engine behind
 * the support page — so the call to action goes there instead of apologising.
 *
 * The exchange shown is a genuine one for this network: a water sensor alert
 * and what to actually do about it. It is illustrative, and labelled as an
 * example, because inventing a transcript that looks like live output would be
 * a small lie told in a product's voice.
 */

const Graci = ({ dealership, utils }) => {

    const mobile = Utils.isMobile();

    const features = [
        'Instant answers on every device in the network',
        'Step-by-step troubleshooting for your exact setup',
        'Included with your network — nothing extra to buy'
    ];

    const onAskClick = () => {
        utils.navigation.set({ key: 'support', view: 'support' });
    }

    const getHeader = () => (
        <div>
            <span style={{
                ...Theme.textStyles.label(),
                display: 'block',
                marginBottom: 14
            }}>{'Meet GRACI'}</span>
            <span style={{
                ...Theme.textStyles.sectionHeading({ textAlign: 'left' }),
                display: 'block',
                marginBottom: mobile ? 0 : 16
            }}>{'Your 24/7 AI safety expert'}</span>
        </div>
    );

    const getBody = () => (
        <div>
            <span style={{
                ...Theme.textStyles.body(),
                display: 'block',
                marginBottom: 28,
                maxWidth: 480
            }}>{'GRACI is trained on every product in the network — every specification, every placement rule, every fix. Ask a question in plain words, describe what a sensor is doing, and get an accurate answer straight away, at whatever hour you happen to need it.'}</span>

            <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 16,
                marginBottom: 32
            }}>
                {features.map((feature, index) => (
                    <div
                    key={index}
                    style={{
                        alignItems: 'center',
                        display: 'flex',
                        gap: 12
                    }}>
                        <Icon
                        color={Theme.colors.primary()}
                        name={'check-circle-fill'}
                        size={18} />
                        <span style={Theme.textStyles.body({ color: Theme.colors.text(), fontWeight: 500 })}>
                            {feature}
                        </span>
                    </div>
                ))}
            </div>
        </div>
    );

    const getChat = () => (
        <div style={{
            alignItems: 'center',
            display: 'flex',
            justifyContent: 'center',
            position: 'relative'
        }}>
            <div style={{
                background: `radial-gradient(circle, ${Theme.tint(0.14)} 0%, rgba(255,255,255,0) 70%)`,
                borderRadius: '50%',
                height: mobile ? 300 : 420,
                pointerEvents: 'none',
                position: 'absolute',
                width: mobile ? 300 : 420
            }} />

            <div style={{
                backgroundColor: Theme.colors.white,
                border: `1px solid ${Theme.colors.border()}`,
                borderRadius: 24,
                boxShadow: Theme.boxShadow.cardHover,
                maxWidth: 420,
                overflow: 'hidden',
                position: 'relative',
                width: '100%'
            }}>
                <div style={{
                    alignItems: 'center',
                    borderBottom: `1px solid ${Theme.colors.border()}`,
                    display: 'flex',
                    gap: 12,
                    padding: '18px 20px'
                }}>
                    <img
                    alt={'GRACI'}
                    src={'images/graci-ai-icon.png'}
                    style={{ height: 42, objectFit: 'contain', width: 42 }} />
                    <div style={{ flexGrow: 1 }}>
                        <span style={{
                            ...Theme.textStyles.body({ color: Theme.colors.text(), fontSize: 15, fontWeight: 700 }),
                            display: 'block'
                        }}>{'GRACI'}</span>
                        <div style={{
                            alignItems: 'center',
                            display: 'flex',
                            gap: 6
                        }}>
                            <span style={{
                                backgroundColor: Theme.colors.success,
                                borderRadius: 4,
                                height: 7,
                                width: 7
                            }} />
                            <span style={Theme.textStyles.caption({ fontSize: 12 })}>
                                {'AI Safety Assistant · Online'}
                            </span>
                        </div>
                    </div>
                </div>

                <div style={{
                    backgroundColor: Theme.colors.offWhite,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 14,
                    padding: 20
                }}>
                    <div style={{
                        alignSelf: 'flex-end',
                        backgroundColor: Theme.colors.primaryFill(),
                        borderBottomRightRadius: 4,
                        borderRadius: 16,
                        maxWidth: '82%',
                        padding: '12px 16px'
                    }}>
                        <span style={Theme.textStyles.body({ color: Theme.colors.onPrimary(), fontSize: 14 })}>
                            {'My water sensor sent me an alert about a leak, what should I do?'}
                        </span>
                    </div>

                    <div style={{
                        alignSelf: 'flex-start',
                        backgroundColor: Theme.colors.white,
                        border: `1px solid ${Theme.colors.border()}`,
                        borderBottomLeftRadius: 4,
                        borderRadius: 16,
                        maxWidth: '88%',
                        padding: '12px 16px'
                    }}>
                        <span style={Theme.textStyles.body({ color: Theme.colors.text(), fontSize: 14 })}>
                            {'Check around the sensor for visible water first. If you find a leak, shut off the main supply before anything else — that stops the damage getting worse while you work out the cause. Then check the sensor and its connections are secure.'}
                        </span>
                    </div>
                </div>

                <div style={{
                    alignItems: 'center',
                    backgroundColor: Theme.colors.white,
                    borderTop: `1px solid ${Theme.colors.border()}`,
                    display: 'flex',
                    gap: 10,
                    padding: '14px 16px'
                }}>
                    <div style={{
                        backgroundColor: Theme.colors.offWhite,
                        borderRadius: 20,
                        flexGrow: 1,
                        padding: '10px 16px'
                    }}>
                        <span style={Theme.textStyles.body({ color: Theme.colors.lightGrey, fontSize: 14 })}>
                            {'Message'}
                        </span>
                    </div>
                    <div style={{
                        alignItems: 'center',
                        backgroundColor: Theme.colors.primaryFill(),
                        borderRadius: 20,
                        display: 'flex',
                        flexShrink: 0,
                        height: 40,
                        justifyContent: 'center',
                        width: 40
                    }}>
                        <Icon color={Theme.colors.onPrimary()} name={'send-fill'} size={14} />
                    </div>
                </div>
            </div>
        </div>
    );

    if(!dealership) {
        return null;
    }

    return (
        <section
        id={'graci'}
        style={{
            backgroundColor: Theme.colors.white,
            width: '100%'
        }}>
            <div style={{
                alignItems: 'center',
                display: 'grid',
                gap: mobile ? 32 : 80,
                gridTemplateColumns: mobile ? '1fr' : '1fr 1fr',
                margin: '0 auto',
                maxWidth: Theme.layout.maxWidth,
                padding: Theme.layout.sectionPadding()
            }}>
                {mobile ? (
                    <>
                        {getHeader()}
                        {getChat()}
                        {getBody()}
                    </>
                ) : (
                    <>
                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center'
                        }}>
                            {getHeader()}
                            {getBody()}
                        </div>
                        {getChat()}
                    </>
                )}
            </div>

            <span style={{
                ...Theme.textStyles.caption({ color: Theme.colors.lightGrey, textAlign: 'center' }),
                display: 'block',
                margin: '0 auto',
                maxWidth: Theme.layout.maxWidth,
                padding: mobile ? '0 20px 32px' : '0 40px 40px'
            }}>{'Example conversation. GRACI answers from the product manuals for the devices actually installed in your home.'}</span>
        </section>
    )
}

export default Graci;
